import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ImportUI from "./ImportUI";
import LocationMapping from "../../LocationAndDepartment/Location/LocationMapping";
import PopupCard from "../../../../Auth/PopupCard";
import { API_URLS } from "../../../../../api/apiUrls";
import { postApi } from "../../../../../api/api_instance";

const normalizeLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getRowValueByHeader = (row, header) => {
  if (!row || typeof row !== "object") {
    return "";
  }

  if (Object.prototype.hasOwnProperty.call(row, header)) {
    return row[header];
  }

  const normalizedHeader = normalizeLabel(header);
  const matchedKey = Object.keys(row).find(
    (key) => normalizeLabel(key) === normalizedHeader,
  );

  return matchedKey ? row[matchedKey] : "";
};

const buildMappingRows = (uploadResponse) => {
  const payload = uploadResponse?.data || {};
  const csvHeaders = Array.isArray(payload?.csvHeaders)
    ? payload.csvHeaders
    : [];
  const sampleData = Array.isArray(payload?.sampleData)
    ? payload.sampleData
    : [];
  const dbFields = Array.isArray(payload?.dbFields) ? payload.dbFields : [];
  const suggestedMappings =
    payload?.suggestedMappings && typeof payload?.suggestedMappings === "object"
      ? payload.suggestedMappings
      : {};

  const rows = csvHeaders.map((header) => ({
    header,
    samples: sampleData
      .map((row) => getRowValueByHeader(row, header))
      .filter(
        (item) => item !== undefined && item !== null && String(item).trim(),
      )
      .slice(0, 3)
      .map((item) => String(item)),
    suggestedAttribute: suggestedMappings[header] || "",
  }));

  return {
    rows,
    attributeOptions: dbFields,
    selections: rows.reduce((acc, row) => {
      acc[row.header] =
        suggestedMappings[row.header] || row.suggestedAttribute || "";
      return acc;
    }, {}),
  };
};

const getMappedSelections = (mappingSelections) =>
  Object.entries(mappingSelections).reduce((acc, [header, attribute]) => {
    if (typeof attribute === "string" && attribute.trim()) {
      acc[header] = attribute;
    }

    return acc;
  }, {});

const getApiErrors = (error, fallbackMessage) => {
  if (typeof error === "string") {
    return [error];
  }

  const errors = Array.isArray(error?.errors)
    ? error.errors.filter((item) => item && String(item).trim())
    : [];

  if (errors.length > 0) {
    return errors.map((item) => String(item));
  }

  if (error?.message) {
    return [String(error.message)];
  }

  return [fallbackMessage];
};

const formatApiError = (error, fallbackMessage) =>
  getApiErrors(error, fallbackMessage).join(", ");

const splitCsvRow = (line) => {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      cells.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  cells.push(current);
  return cells;
};

const encodeCsvCell = (value) => {
  const stringValue = String(value ?? "");
  const escapedValue = stringValue.replace(/"/g, '""');
  return `"${escapedValue}"`;
};

const normalizeCustomerCsvContent = (content) => {
  if (!content) return content;

  const lineBreak = content.includes("\r\n") ? "\r\n" : "\n";
  const rows = content.split(/\r?\n/);
  if (rows.length === 0) return content;

  const headerCells = splitCsvRow(rows[0]);
  const roleIndex = headerCells.findIndex(
    (header) => normalizeLabel(header) === "role",
  );

  if (roleIndex === -1) {
    return content;
  }

  const normalizedRows = rows.map((row, rowIndex) => {
    if (!row) return row;
    if (rowIndex === 0) return row;

    const cells = splitCsvRow(row);
    if (roleIndex >= cells.length) {
      return row;
    }

    const roleValue = String(cells[roleIndex] ?? "").trim();
    if (!roleValue.includes(",")) {
      return row;
    }

    cells[roleIndex] = encodeCsvCell(
      roleValue
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .join(","),
    );

    return cells
      .map((cell, index) => {
        if (index === roleIndex) return cell;

        const rawValue = String(cell ?? "");
        if (
          rawValue.includes(",") ||
          rawValue.includes('"') ||
          rawValue.includes("\n")
        ) {
          return encodeCsvCell(rawValue);
        }

        return rawValue;
      })
      .join(",");
  });

  return normalizedRows.join(lineBreak);
};

const ImportUsers = ({ onClose, isMapping = false }) => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const isMappingStep =
    isMapping || routerLocation.pathname.endsWith("/mapping");
  const routeState = routerLocation.state || {};
  const importInputRef = useRef(null);
  const [selectedImportFile, setSelectedImportFile] = useState(null);
  const [importDocumentName, setImportDocumentName] = useState("");
  const [isImportDragging, setIsImportDragging] = useState(false);
  const [importCurrentStep, setImportCurrentStep] = useState("upload");
  const [importMappingRows, setImportMappingRows] = useState([]);
  const [importAttributeOptions, setImportAttributeOptions] = useState([]);
  const [importMappingSelections, setImportMappingSelections] = useState({});
  const [customerImportData, setCustomerImportData] = useState(null);
  const [importValidationError, setImportValidationError] = useState("");
  const [isParsingCustomerCsv, setIsParsingCustomerCsv] = useState(false);
  const [isUploadingCustomerCsv, setIsUploadingCustomerCsv] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({
    type: "success",
    title: "Success",
    message: "",
  });
  const [popupActions, setPopupActions] = useState(null);

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const triggerPopup = (type, title, message, duration = 2000) => {
    setPopupActions(null);
    setPopupData({ type, title, message });
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), duration);
  };

  const handleOpenImportFileDialog = () => {
    if (importInputRef.current) {
      importInputRef.current.value = "";
      importInputRef.current.click();
    }
  };

  const handleSelectImportFile = (file) => {
    if (!file) return;
    setSelectedImportFile(file);
    setImportDocumentName(file.name.replace(/\.[^/.]+$/, ""));
    setImportCurrentStep("upload");
    setImportMappingRows([]);
    setImportAttributeOptions([]);
    setImportMappingSelections({});
    setCustomerImportData(null);
    setImportValidationError("");
  };

  const handleImportInputChange = (event) => {
    handleSelectImportFile(event.target.files?.[0]);
  };

  const handleImportDrop = (event) => {
    event.preventDefault();
    setIsImportDragging(false);
    handleSelectImportFile(event.dataTransfer.files?.[0]);
  };

  const buildCustomerFileFormData = (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return formData;
  };

  const normalizeCustomerCsvFile = async (file) => {
    if (!file) return file;

    const isCsvFile =
      file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");

    if (!isCsvFile) {
      return file;
    }

    const originalContent = await file.text();
    const normalizedContent = normalizeCustomerCsvContent(originalContent);

    if (normalizedContent === originalContent) {
      return file;
    }

    return new File([normalizedContent], file.name, {
      type: file.type || "text/csv",
      lastModified: file.lastModified,
    });
  };

  const handleImportNext = async () => {
    if (!selectedImportFile) return;

    setIsParsingCustomerCsv(true);
    setImportValidationError("");

    try {
      const normalizedFile = await normalizeCustomerCsvFile(selectedImportFile);
      const response = await postApi({
        apiUrl: API_URLS.CUSTOMER_CSV_UPLOAD,
        data: buildCustomerFileFormData(normalizedFile),
      });

      if (!response?.success) {
        throw response;
      }

      const mappingData = buildMappingRows(response);
      setIsParsingCustomerCsv(false);
      navigate("/organization/user-management/import-users/mapping", {
        state: {
          selectedImportFile: normalizedFile,
          importDocumentName,
          uploadResponse: response,
          mappingData,
        },
      });
    } catch (error) {
      const errorMessage = formatApiError(error, "Customer CSV upload failed.");
      setImportValidationError(errorMessage);
      triggerPopup("error", "Upload Failed", errorMessage);
      setIsParsingCustomerCsv(false);
    }
  };

  useEffect(() => {
    if (isMappingStep) {
      if (routeState?.uploadResponse && routeState?.selectedImportFile) {
        setSelectedImportFile(routeState.selectedImportFile);
        setImportDocumentName(
          routeState.importDocumentName ||
            routeState.selectedImportFile.name.replace(/\.[^/.]+$/, ""),
        );
        const mappingData =
          routeState.mappingData || buildMappingRows(routeState.uploadResponse);
        setImportMappingRows(mappingData.rows);
        setImportAttributeOptions(mappingData.attributeOptions);
        setImportMappingSelections(mappingData.selections);
      } else {
        navigate("/organization/user-management/import-users", {
          replace: true,
        });
      }
    }
  }, [isMappingStep, routeState]);

  const handleImportMappingSelectionChange = (header, value) => {
    setImportMappingSelections((prev) => ({
      ...prev,
      [header]: value,
    }));
    setCustomerImportData(null);
  };

  const performCustomerCsvImport = async () => {
    if (!selectedImportFile) return;

    setIsUploadingCustomerCsv(true);
    setImportValidationError("");
    setShowPopup(false);

    try {
      const normalizedFile = await normalizeCustomerCsvFile(selectedImportFile);
      const formData = new FormData();
      formData.append("file", normalizedFile);
      formData.append(
        "columnMapping",
        JSON.stringify(getMappedSelections(importMappingSelections)),
      );

      const response = await postApi({
        apiUrl: API_URLS.UPLOAD_CUSTOMER_CSV,
        data: formData,
      });

      if (!response?.success) {
        throw response;
      }

      setPopupActions(null);
      setCustomerImportData(response);

      const importPayload = response?.data || {};
      const importValidationErrors = Array.isArray(
        importPayload?.validationErrors,
      )
        ? importPayload.validationErrors
        : [];
      const hasImportErrors =
        importValidationErrors.length > 0 ||
        Number(importPayload?.failedCount || 0) > 0;

      setPopupData({
        type: "success",
        title: "Import Completed",
        message: response?.message || "Users import completed.",
      });
      setShowPopup(true);

      setTimeout(() => {
        setShowPopup(false);
        if (!hasImportErrors) {
          handleClose();
        }
      }, 1800);
    } catch (error) {
      const errorMessage = formatApiError(error, "Customer CSV import failed.");
      setImportValidationError(errorMessage);
      triggerPopup("error", "Import Failed", errorMessage);
    } finally {
      setIsUploadingCustomerCsv(false);
    }
  };

  const handleUploadCustomerCsv = () => {
    setPopupData({
      type: "warning",
      title: "Confirm Import",
      message: "Do you want to import these mapped users?",
    });
    setPopupActions({
      onConfirm: performCustomerCsvImport,
      onCancel: () => {
        setShowPopup(false);
        setPopupActions(null);
      },
      confirmText: "Yes",
      cancelText: "No",
    });
    setShowPopup(true);
  };

  const showUserMappingStep =
    importCurrentStep === "mapping" && importMappingRows.length > 0;

  return (
    <>
      <div className="md:p-6 lg:p-0 bg-[#F5F5F5] mb-20 sm:mb-6 md:mb-6">
        <ImportUI
          heading="Import Users"
          description="Upload your user file and continue with the import flow."
          downloadHref="/samples/User-DOC.csv"
          downloadName="User-DOC.csv"
          onBack={() => {
            if (isMappingStep) {
              navigate("/organization/user-management/import-users");
              return;
            }
            handleClose();
          }}
          inputRef={importInputRef}
          accept=".csv"
          onInputChange={handleImportInputChange}
          showUploader={!isMappingStep}
          openFileDialog={handleOpenImportFileDialog}
          isDragging={isImportDragging}
          onDragOver={(event) => {
            event.preventDefault();
            setIsImportDragging(true);
          }}
          onDragLeave={() => setIsImportDragging(false)}
          onDrop={handleImportDrop}
          selectedFile={selectedImportFile}
          documentName={importDocumentName}
          supportedTypes={[".csv"]}
          onCancel={handleClose}
          onNext={handleImportNext}
          nextDisabled={!selectedImportFile || isParsingCustomerCsv}
          nextLabel={isParsingCustomerCsv ? "Uploading..." : "Next"}
        >
          <LocationMapping
            mappingRows={importMappingRows}
            mappingSelections={importMappingSelections}
            attributeOptions={importAttributeOptions}
            onSelectionChange={handleImportMappingSelectionChange}
            onCancel={() =>
              navigate("/organization/user-management/import-users")
            }
            onImport={handleUploadCustomerCsv}
            importResult={customerImportData}
            importButtonLabel="Import Users"
            backButtonLabel="Back"
            isImporting={isUploadingCustomerCsv}
            validationError={importValidationError}
          />
        </ImportUI>
      </div>

      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <PopupCard
            type={popupData.type}
            title={popupData.title}
            message={popupData.message}
            onConfirm={popupActions?.onConfirm}
            onCancel={popupActions?.onCancel}
            confirmText={popupActions?.confirmText}
            cancelText={popupActions?.cancelText}
          />
        </div>
      )}
    </>
  );
};

export default ImportUsers;
